import { getDisputes } from 'APIs/disputes/disputes';
import { Dispute, DisputesQueryParams } from 'APIs/disputes/disputes.types';
import { getMerchant } from 'APIs/merchants/merchants';
import { AnyMerchant } from 'APIs/merchants/merchants.types';
import { administerTransaction, getTransaction } from 'APIs/transactions/transactions';
import { TransactionResponse } from 'APIs/transactions/transactions.types';
import { QueryParams } from 'APIs/types';
import { FilterOption } from 'Components/Common/Filters/types';
import { TransactionAPIResponseType } from 'Components/Transactions/types';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';
import getErrorMessage from 'utils/getErrorMessage';
import getFetchOptions from 'utils/getFetchOptions';
import handleApiError from 'utils/handleApiError';

import { filterQueryParams, initFilterOptions } from './DisputeFilter';
import DisputesList from './DisputesList';
import { SetDisputeOutcomeFormProps, toForceDisputeOutcomeRequest } from './types';

type Props = {
  merchantId?: string;
  transactionId?: string;
};

const ManageDisputes = ({ merchantId, transactionId }: Props) => {
  const { apiAprilBaseUri, apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiAprilBaseUri: state.apiAprilBaseUri,
    apiBaseUri: state.apiBaseUri,
  }));

  const [disputes, setDisputes] = useState<Array<Dispute>>([]);
  const [filterOptionValues, setFilterOptionValues] = useState<FilterOption[]>(initFilterOptions);
  const [activePage, setActivePage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchingError, setFetchingError] = useState<string>('');
  const [transaction, setTransaction] = useState<TransactionResponse | null>(null);
  const [merchant, setMerchant] = useState<AnyMerchant | null>(null);
  const [payment, setPayment] = useState<TransactionAPIResponseType | null>(null);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');
  const [detailsDispute, setDetailsDispute] = useState<Dispute | null>(null);
  const [modalDispute, setModalDispute] = useState<Dispute | null>(null);

  const getFilterQueryParams = useCallback(filterQueryParams, []);

  const fetchDisputes = useCallback(async () => {
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      setIsLoading(true);
      const qParams: QueryParams = getFilterQueryParams(filterOptionValues);

      const resp = await getDisputes(apiAprilBaseUri, {
        transactionId,
        merchantId,
        ...(!transactionId && !merchantId ? (qParams as DisputesQueryParams) : {}),
        page: activePage,
        limit: perPage,
        sort: '-updatedAt',
      });
      setDisputes(resp.items);
      setTotalCount(resp.totalRecords);
    } catch (e) {
      setDisputes([]);
      setTotalCount(0);
      setFetchingError(getApiErrorMessage(e));
      toast.error(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [activePage, apiAprilBaseUri, filterOptionValues, getFilterQueryParams, merchantId, perPage, transactionId]);

  const fetchPayment = useCallback(
    async (transactionId: string) => {
      try {
        const resp = await getTransaction(apiAprilBaseUri, transactionId);
        setTransaction(resp);

        const url = `${apiBaseUri}/dashboard/merchant/${resp.purchaseDetails.merchantId}/payments/query?transactionIds=${transactionId}`;
        const options = await getFetchOptions();
        const res = await fetch(url, options);
        if (!res.ok) {
          await handleApiError(res);
        }
        const json = await res.json();
        setPayment(json?.[0] || null);
      } catch (e) {
        toast.error(getApiErrorMessage(e));
      }
    },
    [apiAprilBaseUri, apiBaseUri],
  );

  const fetchMerchant = useCallback(async () => {
    if (!apiAprilBaseUri || !transaction) {
      return;
    }

    try {
      const resp = await getMerchant(apiAprilBaseUri, transaction.purchaseDetails.merchantId);
      setMerchant(resp);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  }, [apiAprilBaseUri, transaction]);

  const updateDispute = useCallback(
    async (formData: SetDisputeOutcomeFormProps) => {
      if (!modalDispute) {
        return;
      }
      const confirmed = window.confirm(
        `Are you sure you want to update the dispute outcome as "${formData.disputeOutcome}"?`,
      );
      if (!confirmed) {
        return;
      }
      const payload = toForceDisputeOutcomeRequest(formData);
      setModalLoading(true);
      setModalErrorMsg('');
      try {
        const resp = await administerTransaction(apiAprilBaseUri, modalDispute.transactionId, payload);
        setTransaction(resp);
        fetchDisputes();
        if (detailsDispute) {
          setDetailsDispute((prev) =>
            prev ? { ...prev, outcome: formData.disputeOutcome, state: formData.state || '' } : null,
          );
        }
        setModalDispute(null);
        toast.success('Dispute outcome updated successfully');
      } catch (e) {
        setModalErrorMsg(getApiErrorMessage(e));
      } finally {
        setModalLoading(false);
      }
    },
    [modalDispute, apiAprilBaseUri, fetchDisputes, detailsDispute],
  );

  const handlePerPageChange = useCallback((newPerPage: number, page: number) => {
    setPerPage(newPerPage);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page);
  }, []);

  const handleClickBack = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    setDetailsDispute(null);
    setTransaction(null);
    setMerchant(null);
    setPayment(null);
  };

  useEffect(() => {
    fetchDisputes();
  }, [activePage, perPage, fetchDisputes]);

  useEffect(() => {
    if (detailsDispute) {
      fetchPayment(detailsDispute.transactionId);
    }
  }, [detailsDispute, fetchPayment]);

  useEffect(() => {
    if (transaction) {
      fetchMerchant();
    }
  }, [detailsDispute, fetchMerchant, fetchPayment, transaction]);

  return (
    <DisputesList
      disputes={disputes}
      totalCount={totalCount}
      perPage={perPage}
      isLoading={isLoading}
      activePage={activePage}
      fetchingError={fetchingError}
      showFilterButton={!transactionId && !merchantId}
      initFilterOptions={initFilterOptions}
      filterOptionValues={filterOptionValues}
      modalErrorMsg={modalErrorMsg}
      modalDispute={modalDispute}
      modalLoading={modalLoading}
      detailsDispute={detailsDispute}
      payment={payment}
      merchant={merchant}
      fetchDisputes={fetchDisputes}
      setActivePage={setActivePage}
      handlePageChange={handlePageChange}
      handlePerPageChange={handlePerPageChange}
      setDetailsDispute={setDetailsDispute}
      setModalDispute={setModalDispute}
      handleClickBack={handleClickBack}
      updateDispute={updateDispute}
      setFilterOptionValues={setFilterOptionValues}
    />
  );
};

export default ManageDisputes;
